import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { ISweetAlertService, SweetAlertService } from '@/app-modules/sweet-alert-service';
import { SweetAlertOptions } from 'sweetalert2';
import { Lang } from 'vuejs-localization';
import { IHttpRequest, IHttpService } from '@/app-modules/http';
import { base64 } from '@/app-modules/utils';
import { container } from '@/app-modules/_ioc';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { User } from '@/app-modules/store/modules/user';
import { IGetNotesResponse, IGetNoteDetailResponse, IPostNoteRequest, IPostNoteResponse,
         IPutNoteRequest, IPutNoteResponse, IPlayerNote } from '@/components/chat/_domain/models/player-notes-model';

interface IPlayerNotesService {
  getPlayerNotesByUsername(username: string): Promise<IGetNotesResponse>;
  getPlayerNoteDetail(noteId: string): Promise<IGetNoteDetailResponse>;
  deletePlayerNote(noteToDelete: IPlayerNote, playerUsername: string): Promise<boolean>;
  addPlayerNote(playerUsername: string, value: string): Promise<IPostNoteResponse>;
  updatePlayerNote(noteId: string, value: string, playerUsername: string): Promise<IPutNoteResponse>;
}

@injectable()
class PlayerNotesService implements IPlayerNotesService {
  private readonly $lang: Lang;

  constructor(
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService
  ) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public async getPlayerNotesByUsername(username: string): Promise<IGetNotesResponse> {
    return await this.sendGetPlayerNotesCommandForUser(username);
  }

  public async getPlayerNoteDetail(noteId: string): Promise<IGetNoteDetailResponse> {
    return await this.sendGetPlayerNoteDetailsCommand(noteId);
  }

  public async addPlayerNote(playerUsername: string, value: string): Promise<IPostNoteResponse> {
    return await this.sendAddPlayerNoteCommand(playerUsername, value);
  }

  public async deletePlayerNote(noteToDelete: IPlayerNote, playerUsername: string): Promise<boolean> {
    let noteSummary: string;

    if (noteToDelete.Value.length > 10) {
      noteSummary = noteToDelete.Value.substr(0, 10) + '...';
    } else {
      noteSummary = noteToDelete.Value;
    }

    const result = await this.sweetAlertService.show({
      title: this.$lang.chat.playernotes.deletetitle,
      text:  this.$lang.chat.playernotes.deletetext.format({noteSummary}),
      type: 'warning',
      allowOutsideClick: false,
      focusConfirm: false,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: this.$lang.chat.playernotes.deletebuttontext,
      confirmButtonColor: '#dc3545'
    });

    if (result.value === true) {
      try {
        const response = await this.sendDeletePlayerNoteCommand(noteToDelete.NoteId, playerUsername);
        return response;
      } catch (error) {
        await this.sweetAlertService.show({
          title: this.$lang.chat.playernotes.deleteerrortitle,
          text: this.$lang.chat.playernotes.deleteerrorconfirm.format({noteSummary}),
          type: 'error',
          allowOutsideClick: false,
          focusConfirm: false,
          showConfirmButton: true
        });
        return false;
      }
    }
  }

  public async updatePlayerNote(noteId: string, value: string, playerUsername: string): Promise<IPutNoteResponse> {
    return await this.sendUpdatePlayerNoteCommand(noteId, value, playerUsername);
  }

  private async sendGetPlayerNotesCommandForUser(username: string): Promise<IGetNotesResponse> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);

    const httpRequest = this.getHttpRequestForPlayerNotesRequest(username, cmUser);
    const response = await this.httpService.get(httpRequest);
    return response.data.Data as IGetNotesResponse;
  }

  private async sendGetPlayerNoteDetailsCommand(noteId: string): Promise<IGetNoteDetailResponse> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);

    const httpRequest = this.getHttpRequestForPlayerNoteDetailRequest(noteId, cmUser);
    const response = await this.httpService.get(httpRequest);
    return response.data.Data as IGetNoteDetailResponse;
  }

  private async sendAddPlayerNoteCommand(playerUsername: string, value: string): Promise<IPostNoteResponse> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);

    const httpRequest = this.getHttpRequestForAddPlayerNoteRequest(playerUsername, value, cmUser);
    const response = await this.httpService.post(httpRequest);
    return response.data.Data as IPostNoteResponse;
  }

  private async sendDeletePlayerNoteCommand(noteId: string, playerUsername: string): Promise<boolean> {
    const httpRequest = this.getHttpRequestForDeletePlayerNoteRequest(noteId, playerUsername);
    const response = await this.httpService.delete(httpRequest);
    return response.status === 200;
  }

  private async sendUpdatePlayerNoteCommand(noteId: string, value: string, playerUsername: string): Promise<IPutNoteResponse> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);

    const httpRequest = this.getHttpRequestForUpdatePlayerNoteRequest(noteId, value, playerUsername, cmUser);
    const response = await this.httpService.put(httpRequest);
    return response.data.Data as IPutNoteResponse;
  }

  private getHttpRequestForPlayerNotesRequest(username: string, cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const httpRequest: IHttpRequest = {
      Url: `/playernotes/notes/${username}`,
      XChatModsAuthorization: encodedAuth
    };
    return httpRequest;
  }

  private getHttpRequestForPlayerNoteDetailRequest(noteId: string, cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const httpRequest: IHttpRequest = {
      Url: `/playernotes/notedetail/${noteId}`,
      XChatModsAuthorization: encodedAuth,
    };
    return httpRequest;
  }

  private getHttpRequestForDeletePlayerNoteRequest(noteId: string, playerUsername: string): IHttpRequest {
    const httpRequest: IHttpRequest = {
      Url: `/playernotes/note/${playerUsername}/${noteId}`
    };
    return httpRequest;
  }

  private getHttpRequestForAddPlayerNoteRequest(playerUsername: string, value: string, cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const postNoteRequest: IPostNoteRequest = {
      PlayerUsername: playerUsername,
      Value: value
    };
    const httpRequest: IHttpRequest = {
      Url: `/playernotes/note`,
      XChatModsAuthorization: encodedAuth,
      Payload: postNoteRequest
    };
    return httpRequest;
  }

  private getHttpRequestForUpdatePlayerNoteRequest(noteId: string, note: string, playerUsername: string,  cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const putNoteRequest: IPutNoteRequest = {
      PlayerUsername: playerUsername,
      Value: note
    };
    const httpRequest: IHttpRequest = {
      Url: `/playernotes/note/${playerUsername}/${noteId}`,
      XChatModsAuthorization: encodedAuth,
      Payload: putNoteRequest
    };
    return httpRequest;
  }
}

export { IPlayerNotesService, PlayerNotesService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/player-notes-service/player-notes-service.ts